import { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useApiService, ArtTable, ArtTableHandle, SSRTableBuilder, HeadCell, FormFieldConsolidatedProps } from '@art/shared-ui';
import { useSearchParams } from 'react-router-dom';
import { usePageWrapper } from '../../../hooks';
import { tableParamNav } from "../../../utils/table-utils";

type BillingInvoice = {
    id: string;
    date: string;
    invoice_type: string;
    plan: string;
    quantity: number;
    amount: string;
    status: string;
    downloadUrl: string;
    payment_proof_url: string;
};

type InvoiceResponse = {
    invoices: BillingInvoice[];
    total_count: number;
};

function BillingHistory() {
    const tableRef = useRef<ArtTableHandle>(null);
    const apiservice = useApiService();
    const [queryParams, setQueryParams]: any = useSearchParams();

    const headCells: HeadCell<BillingInvoice>[] = [
        {
            id: "id",
            numeric: false,
            disablePadding: false,
            label: "Invoice",
            getColData: (row) => row.id,
        },
        {
            id: "invoice_type",
            numeric: false,
            disablePadding: false,
            label: "Type",
            getColData: (row) => row.invoice_type,
        },
        {
            id: "plan",
            numeric: false,
            disablePadding: false,
            label: "Plan",
            getColData: (row) => row.plan || '-',
        },
        {
            id: "date",
            numeric: false,
            disablePadding: false,
            label: "Purchased Date",
            getColData: (row) => (row.date ? new Date(row.date).toLocaleDateString() : '-'),
        },
        {
            id: "quantity",
            numeric: true,
            disablePadding: false,
            label: "Quantity",
            getColData: (row) => row.quantity || 1,
        },
        {
            id: "amount",
            numeric: false,
            disablePadding: false,
            label: "Amount",
            getColData: (row) => row.amount,
        },
        {
            id: "status",
            numeric: false,
            disablePadding: false,
            label: "Status",
            getColData: (row) => row.status,
        },
        {
            id: "payment_proof_url",
            numeric: false,
            disablePadding: false,
            label: "Proof",
            getColData: (row) =>
                row.payment_proof_url ? (
                    <a href={row.payment_proof_url} target="_blank" rel="noreferrer">
                        View Proof
                    </a>
                ) : (
                    '-'
                ),
        },
        {
            id: "downloadUrl",
            numeric: false,
            disablePadding: false,
            label: "Action",
            getColData: (row) => (
                <a href={row.downloadUrl} download>
                    {row.downloadUrl ? 'Download' : 'N/A'}
                </a>
            ),
        },
];

    const TYPE_OPTIONS = [
        { label: "All", value: "all" },
        { label: "ADDON-INVOICE", value: "ADDON-INVOICE" },
        { label: "FINAL-INVOICE", value: "FINAL-INVOICE" },
    ];

    const [formKey, setFormKey] = useState(0);

    const tableBuilder = SSRTableBuilder<BillingInvoice>({
        endpoint: "/invoice-management/invoices",
        method: "get",
        apiService: apiservice,

        onFetch: (params) => {
            let currentPage = params["page"] || "1";
            let searchText = params["search_text"] || "";
            let invoiceType = params["invoice_type"] || "all";

            const newParams: any = {
                page: currentPage,
            };

            if (invoiceType && invoiceType !== 'all') {
                newParams.invoice_type = invoiceType;
            }

            if (searchText) {
                newParams.search = searchText;
            }

            setQueryParams(newParams, tableParamNav(queryParams, "page", currentPage));
            return {
                page: currentPage,
                search_text: searchText,
                invoice_type: invoiceType,
            };
        },
        onResponse: (data) => {
            const payload = data.data;
const responseData: InvoiceResponse = Array.isArray(payload)
                ? { invoices: payload, total_count: payload.length }
                : payload;

            return {
                data: responseData?.invoices || [],
                total: responseData?.total_count || 0,
            };
        },
    });

    const filterBuilder = useCallback(() => {
        return [
            {
                id: "invoice_type",
                type: "select",
                label: "Invoice Type",
                getOptions: async () => TYPE_OPTIONS,
                getOptionLabel: (data: any) => data.label,
                getOptionValue: (data: any) => data.value,
                marginBottom: 14,
            },
            {
                type: "row",
                classNames: [""],
                children: [
                    {
                        type: "col",
                        classNames: ["col-auto"],
                        children: [
                            {
                                type: "submit-button",
                                label: "Filter",
                            },
                        ],
                    },
                    {
                        type: "col",
                        classNames: ["col-auto"],
                        children: [
                            {
                                type: "reset-button",
                                label: "Reset",
                                onClick: () => {
                                    setFormKey((prev) => prev + 1);
                                },
                            },
                        ],
                    },
                ],
            },
        ] as FormFieldConsolidatedProps[];
    }, []);

    return (
        <div>
            <Typography variant="h4">Billing History</Typography>
            <Typography variant="caption" color="text.secondary">
                Review your invoices, filter by type, and open payment proof or PDF copies when available.
            </Typography>

            <Box mb={2} mt={3}>
                <ArtTable
                    key={formKey}
                    ref={tableRef}
                    headCells={headCells}
                    builder={tableBuilder}
                    search={{
                        enabled: true,
                        value: queryParams.get("search") || ""
                    }}
                    pagination={{
                        initialPage: parseInt(queryParams.get("page") || "1") - 1,
                    }}
                    filter={{
                        enabled: true,
                        builder: filterBuilder,
                    }}
                />
            </Box>
        </div>
    );
}

export default usePageWrapper(BillingHistory, {
    page_title: "Invoices",
});
